import React, { useState ,useEffect, useRef, useMemo, useCallback} from 'react'
import SweetAlertService from '../../../../services/lib/utils/sweet-alert-service'
import CommonTablePage, { CommonTablePagePropsInterface } from '../../../../components/common/common-table/common-table-page'
import SalesOrderManagementColumnModel
, { renderOrderProduct ,myfiler, applyOrderDateRangeFilter} from '../../../order/sales-order/sales-order-management-column-model'
import { urlKey } from '../../../../services/api/api-urls'
import CommonDialog from '../../../../components/common/others/common-dialog'
// import DispatchedTable from './dispatched-table/dispatched-table'
import { ApiRequest } from '../../../../services/api/api'
import { getRandomKey } from '../../../../services/lib/utils/helpers'
import SalesOrderProductManagementDialog
  from '../../../order/sales-order/sales-order-product-management-dialog/sales-order-product-management-dialog'
import { IconButton } from '@material-ui/core'
import { useDocumentTitle } from '../../../../services/title/useDocumentTitle'

/**
 * 计算取整库存（向上取整到整箱数量）
 */
const getRoundedStockQuantity = (stockQuantity: number, packagingQuantity: number): number => {
  if (!stockQuantity || stockQuantity <= 0) return 0;
  if (!packagingQuantity || packagingQuantity <= 0) return stockQuantity;
  const roundedBoxes = Math.ceil(stockQuantity / packagingQuantity);
  return roundedBoxes * packagingQuantity;
};

/**
 * 检查订单产品是否为绿色（库存充足）
 */
const isProductStockGreen = (product: any): boolean => {
  const packagingQuantity = product.product?.baseProduct?.packagingType?.quantity || 1;
  const requiredQuantity = product.quantity * packagingQuantity;
  const availableStock = requiredQuantity - (product.deliveredQuantity || 0);
  const roundedStockQuantity = getRoundedStockQuantity(product.stockQuantity || 0, packagingQuantity);
  return roundedStockQuantity >= availableStock;
};

/**
 * 检查多选订单是否有库存不足的绿色产品
 * @param selectedOrders 选中的订单列表
 * @param pickupListOrders 已在 Pick List 中的订单列表（可选）
 * @returns 库存不足的产品列表，如果没有则返回空数组
 */
const checkStockInsufficientForMultipleOrders = (
  selectedOrders: any[],
  pickupListOrders: any[] = []
): Array<{
  productId: number
  productCode: string
  productName: string
  totalRequiredBoxes: number
  pickupListOccupiedBoxes: number
  totalStockBoxes: number
  orders: Array<{orderNo: string, poNo: string, customerName: string, requiredBoxes: number}>
  pickupListOrders: Array<{orderNo: string, poNo: string, customerName: string, occupiedBoxes: number}>
}> => {
  const stockInsufficientProducts = new Map<number, {
    productId: number
    productCode: string
    productName: string
    totalRequiredBoxes: number
    pickupListOccupiedBoxes: number
    totalStockBoxes: number
    orders: Array<{orderNo: string, poNo: string, customerName: string, requiredBoxes: number}>
    pickupListOrders: Array<{orderNo: string, poNo: string, customerName: string, occupiedBoxes: number}>
  }>()

  // 首先收集已在 Pick List 中的订单占用的库存
  const pickupListOccupiedMap = new Map<number, number>(); // productId -> occupied boxes

  for (const order of pickupListOrders) {
    if (!order.orderProduct || !Array.isArray(order.orderProduct)) continue;

    for (const product of order.orderProduct) {
      if (!isProductStockGreen(product)) continue;

      const packagingQuantity = product.product?.baseProduct?.packagingType?.quantity || 1;
      const requiredQuantity = product.quantity * packagingQuantity;
      const availableStock = requiredQuantity - (product.deliveredQuantity || 0);
      const requiredBoxes = Math.ceil(availableStock / packagingQuantity);

      if (requiredBoxes > 0) {
        pickupListOccupiedMap.set(
          product.productId,
          (pickupListOccupiedMap.get(product.productId) || 0) + requiredBoxes
        );
      }
    }
  }

  // 遍历当前选中的订单，收集每个绿色产品的需求
  for (const order of selectedOrders) {
    if (!order.orderProduct || !Array.isArray(order.orderProduct)) continue;

    for (const product of order.orderProduct) {
      if (!isProductStockGreen(product)) continue;

      const packagingQuantity = product.product?.baseProduct?.packagingType?.quantity || 1;
      const requiredQuantity = product.quantity * packagingQuantity;
      const availableStock = requiredQuantity - (product.deliveredQuantity || 0);
      const roundedStockQuantity = getRoundedStockQuantity(product.stockQuantity || 0, packagingQuantity);

      const requiredBoxes = Math.ceil(availableStock / packagingQuantity);
      const stockBoxes = Math.ceil(roundedStockQuantity / packagingQuantity);
      const occupiedBoxes = pickupListOccupiedMap.get(product.productId) || 0;

      if (!stockInsufficientProducts.has(product.productId)) {
        stockInsufficientProducts.set(product.productId, {
          productId: product.productId,
          productCode: product.product?.productCode || '',
          productName: product.product?.productName || '',
          totalRequiredBoxes: 0,
          pickupListOccupiedBoxes: occupiedBoxes,
          totalStockBoxes: stockBoxes,
          orders: [],
          pickupListOrders: []
        });
      }

      const productInfo = stockInsufficientProducts.get(product.productId)!;
      productInfo.totalRequiredBoxes += requiredBoxes;

      // 只有当需要拣货的箱数大于 0 时，才将订单添加到列表中
      if (requiredBoxes > 0) {
        productInfo.orders.push({
          orderNo: order.orderNo,
          poNo: order.custOrderNo || '',
          customerName: order.customer?.company || '',
          requiredBoxes: requiredBoxes
        });
      }
    }
  }

  // 为已在 Pick List 中的订单添加详细信息
  for (const order of pickupListOrders) {
    if (!order.orderProduct || !Array.isArray(order.orderProduct)) continue;

    for (const product of order.orderProduct) {
      if (!isProductStockGreen(product)) continue;
      if (!stockInsufficientProducts.has(product.productId)) continue;

      const packagingQuantity = product.product?.baseProduct?.packagingType?.quantity || 1;
      const requiredQuantity = product.quantity * packagingQuantity;
      const availableStock = requiredQuantity - (product.deliveredQuantity || 0);
      const requiredBoxes = Math.ceil(availableStock / packagingQuantity);

      if (requiredBoxes > 0) {
        const productInfo = stockInsufficientProducts.get(product.productId)!;

        // 避免重复添加同一个订单
        const orderExists = productInfo.pickupListOrders.some(o => o.orderNo === order.orderNo);
        if (!orderExists) {
          productInfo.pickupListOrders.push({
            orderNo: order.orderNo,
            poNo: order.custOrderNo || '',
            customerName: order.customer?.company || '',
            occupiedBoxes: requiredBoxes
          });
        }
      }
    }
  }

  // 筛选出库存不足的产品（考虑 Pick List 占用后）
  const insufficientList: typeof stockInsufficientProducts extends Map<infer K, infer V> ? V[] : never = [];

  stockInsufficientProducts.forEach((info) => {
    const totalOccupied = info.totalRequiredBoxes + info.pickupListOccupiedBoxes;
    if (totalOccupied > info.totalStockBoxes) {
      insufficientList.push(info);
    }
  });

  return insufficientList;
};

/**
 * 生成库存不足的提示消息
 */
const generateStockInsufficientMessage = (insufficientList: Array<{
  productCode: string
  totalRequiredBoxes: number
  pickupListOccupiedBoxes: number
  totalStockBoxes: number
  orders: Array<{orderNo: string, poNo: string, customerName: string, requiredBoxes: number}>
  pickupListOrders: Array<{orderNo: string, poNo: string, customerName: string, occupiedBoxes: number}>
}>): string => {
  return insufficientList.map(item => {
    const ordersList = item.orders
      .map(o => {
        const poDisplay = o.poNo ? ` <span style="color: #666;">(PO: ${o.poNo})</span>` : '';
        return `<div style="margin-left: 20px;">• ${o.orderNo}${poDisplay} - ${o.customerName} (${o.requiredBoxes} boxes)</div>`;
      })
      .join('');

    const pickupListSection = item.pickupListOrders.length > 0 ? `
      <div style="margin-top: 8px; padding-left: 20px; border-left: 3px solid #FFA500;">
        <div style="color: #FFA500; font-weight: 600;">⚠️ Already in Pick List (${item.pickupListOccupiedBoxes} boxes occupied):</div>
        ${item.pickupListOrders.map(o => {
          const poDisplay = o.poNo ? ` <span style="color: #666;">(PO: ${o.poNo})</span>` : '';
          return `<div style="margin-left: 20px;">• ${o.orderNo}${poDisplay} - ${o.customerName} (${o.occupiedBoxes} boxes)</div>`;
        }).join('')}
      </div>
    ` : '';

    const totalRequired = item.totalRequiredBoxes + item.pickupListOccupiedBoxes;
    const isShortage = totalRequired > item.totalStockBoxes;

    return `
      <div style="margin-bottom: 15px;">
        <strong>${item.productCode}</strong><br/>
        <div style="margin-left: 20px;">
          <div>Total Stock: <span style="color: #28a745; font-weight: 600;">${item.totalStockBoxes} boxes</span></div>
          <div>Pick List Occupied: <span style="color: #FFA500; font-weight: 600;">${item.pickupListOccupiedBoxes} boxes</span></div>
          <div>Current Required: <span style="font-weight: 600;">${item.totalRequiredBoxes} boxes</span></div>
          <div style="margin-top: 4px;">Total Required: <span style="${isShortage ? 'color: red;' : 'color: #28a745;'} font-weight: 600;">${totalRequired} boxes</span></div>
          ${isShortage ? `<div style="color: red; font-weight: 600;">Shortage: ${totalRequired - item.totalStockBoxes} boxes</div>` : ''}
        </div>
        <div style="margin-top: 8px;">
          <div style="font-weight: 600;">Current Orders:</div>
          ${ordersList}
        </div>
        ${pickupListSection}
      </div>
    `;
  }).join('');
};


const refreshTime=5;

// 独立的倒计时显示组件，state 变化不会导致父组件重新渲染
// 防止 MaterialTable 因父组件 re-render 重复调用 setColumns 导致 width 字符串指数增长
const CountdownDisplay = ({ refreshMinutes, isDialogOpen }: { refreshMinutes: number, isDialogOpen: boolean }) => {
  const [value, setValue] = useState(refreshMinutes);

  useEffect(() => {
    const timer = setInterval(() => {
      setValue(prev => {
        if (isDialogOpen) return refreshMinutes;
        if (prev <= 1) return refreshMinutes;
        return prev - 1;
      });
    }, 60000);
    return () => clearInterval(timer);
  }, [isDialogOpen, refreshMinutes]);

  if (value >= refreshMinutes) return null;
  return (
    <div style={{position:'fixed',marginTop:"0px",right:"400px",zIndex:999,border:"solid 1px",padding:"10px",color:"red"}}>
      Refresh in {value} minutes!
    </div>
  );
};

const AwaitingDispatchList = (props: {userId?: any,pickupFlag?:number}) => {
  const [triggerResetData, setTriggerResetData] = useState<any>(false)
  const [isShowSpinner, setIsShowSpinner] = useState(true)
  const [open, setOpen] = useState(false)
  const [dialogTitle, setDialogTitle] = useState<string>()
  const [dialogContent, setDialogContent] = useState<any>()

  const [deliveryMethodLookup, setDeliveryMethodLookup] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const openRef = useRef(open);
  openRef.current = open;

    // 获取配送方式的 lookup 数据
  useEffect(() => {
    setIsLoading(true);
    ApiRequest({
      url: 'DeliveryMethod/GetAllDeliveryMethod',
      method: 'get'
    })
      .then((response) => {
        const lookup: any = {};

        // response.data.data 才是真正的 array
        const methods = response?.data?.data ?? [];

        methods.forEach((method: any) => {
          lookup[method.deliveryMethodId] = method.deliveryMethodName;
        });

        setDeliveryMethodLookup(lookup);
        console.log("lookup", lookup);
      })
      .catch((error) => {
        console.error('Failed to fetch delivery methods:', error);
      })
      .finally(() => {
      setIsLoading(false);
    });
  }, []);

  useDocumentTitle();

  // 每 5 分钟刷新一次数据，不使用 60 秒倒计时 state 避免频繁 re-render
  useEffect(() => {
    const timer = setInterval(() => {
      if (!openRef.current) {
        setTriggerResetData(getRandomKey());
      }
    }, refreshTime * 60000);
    return () => clearInterval(timer);
  }, []);


  const setColumnModel = () => {
    let newModel: any = [...SalesOrderManagementColumnModel('awaiting', deliveryMethodLookup)]
      .filter((row: any) => 
        row.field !== 'operEmployeeName' &&
        row.field !== 'CreatedEmployeeName' &&
        row.field !== 'paid' && 
        row.field !== 'stage'
      )

    // let index = newModel.findIndex((e:any)=>{
    //   return e.title == 'Required Date'
    // })
    // newModel.splice(index,1)

    let index2 = newModel.findIndex((e: any) => e?.title == 'Location');
    if (index2 > -1 && newModel[index2]) {
      newModel[index2].lookup = {
      1: 'New',
      3: 'Approved',
      10: 'Production',
      11: 'Warehouse',
      12: 'Dispatch'
      };
    }
    // newModel.push({
    //   title: 'Urgent',
    //   field: 'deliveryAsap',
    //   // defaultSort: 'desc',
    //   lookup: {0: 'No', 1: 'Yes'},
    //   render: (rowData: any) => rowData.deliveryAsap ? <b style={{color: 'red'}}>Yes</b> : <span>No</span>
    // })

    newModel = newModel.map((row: any) => (
      row.title === 'Items' &&
      ({
        ...row,
        filtering: true,
        editable: 'never',        
        // lookup: {0: 'Not Dispatch', 1: 'Dispatching'},
        // customFilterAndSearch: (filterValue: any, rowData: any) => {
        //   let result=false;
        //   rowData.orderProduct.map((e:any)=>{
        //     if (e?.product?.productName?.toUpperCase().includes(filterValue.toUpperCase())) result=true;
        //   })
        //   return result
        // },
        customFilterAndSearch: (filterValue: any, rowData: any) =>myfiler(filterValue,rowData),
        render: (rowData:any) => renderOrderProduct(rowData, true)
      })) || row
    )
    newModel = newModel.map((row: any) => row.title === 'Note' ? { ...row, cellStyle: { minWidth: '16rem' }, headerStyle: { minWidth: '16rem' } } : row)

    // Order Date / REQ Date 使用日期段（Start date ~ End date）过滤
    newModel = applyOrderDateRangeFilter(newModel)
    return newModel
  }

  const onDialogClose = (isModified: boolean) => {
    setIsShowSpinner(false)
    setOpen(false)
    setTriggerResetData(getRandomKey())
  }

  // const dispatchedDialog = (orderData: any) => <DispatchedTable orderId={orderData?.orderId} />
  const salesOrderProductManagementDialog = (orderData: any) => <SalesOrderProductManagementDialog isAwaitingDispatchPage={true} isDispatchPage={true} isPicking={props.pickupFlag==1} isNewOrder={false} onDialogClose={onDialogClose} orderData={orderData} />

  // 使用 useMemo 稳定 actionButtons 引用
  const actionButtons: any = useMemo(() => [
    {
      icon: 'ghost', //Button attr of Ant design (danger, ghost)
      tooltip: 'Finish',
      position: 'row', // 单行操作按钮
      onClick: async (event: any, rowData: any) => {
        const result = await SweetAlertService.confirmMessage()
        if (result) {
          ApiRequest({
            url: 'SalesOrder/FinishDispatch?id=' + rowData.orderId,
            method: 'put'
          }).then(_ => {
            setTriggerResetData(getRandomKey())
          })
        }
      }
    },
    {
      icon: '', //Button attr of Ant design (danger, ghost)
      tooltip: 'Handle',
      position: 'row',
      // isFreeAction: false,
      onClick: (event: any, rowData: any) => {
        setOpen(true)
        setDialogTitle('Handle Dispatch')
        setDialogContent(salesOrderProductManagementDialog(rowData))
      }
    },
    {
      icon: 'ghost', //Button attr of Ant design (danger, ghost)
      tooltip: props.pickupFlag?'To Dispatching':'To Picking',
      // position: 'row',
      isFreeAction: false,
      onClick: async (event: any, rowData: any) => {
        // 统一的初始确认
        const result = await SweetAlertService.confirmMessage()
        if (!result) {
          return; // 用户取消，直接退出
        }

        if (props.pickupFlag) {
          // 已经在 Picking 页面，执行原来的逻辑
          let ordersId: any[] = [];
          rowData.map((e:any)=>{
            ordersId.push(e.orderId);
          })
          let url = `SalesOrder/ChangeOrderPickup?pickupFlag=0`;
          ApiRequest({
            url: url,
            method: 'put',
            data: ordersId,
          }).then(_ => {
            setTriggerResetData(getRandomKey())
          })
          return
        }

        // 从 Awaiting Dispatch 进入 Picking，需要检查库存
        // 首先获取已在 Pick List 中的订单，以计算已占用的库存
        let pickupListOrders: any[] = [];
        try {
          const pickupListResponse = await ApiRequest({
            url: 'SalesOrder/GetPickupableOrder',
            method: 'get'
          });
          pickupListOrders = pickupListResponse?.data?.data || [];
        } catch (error) {
          console.error('Failed to fetch pickup list orders:', error);
          // 如果获取失败，继续执行但使用空数组
        }

        const insufficientList = checkStockInsufficientForMultipleOrders(rowData, pickupListOrders);

        if (insufficientList.length > 0) {
          // 有库存不足的产品，显示警告
          const message = generateStockInsufficientMessage(insufficientList);

          const result = await SweetAlertService.confirmMessage3(
            `<div style="text-align: left; line-height: 1.6;">
              <strong style="color: red;">⚠️ Stock Insufficient Warning:</strong><br/><br/>
              The following products have insufficient stock when considering both:<br/>
              • Current selected orders<br/>
              • Orders already in Pick List<br/><br/>
              ${message}
              <br/>
              <strong>Please select an option:</strong>
            </div>`,
            'Cancel All',
            'Pick Available Only',
            'Continue Anyway'
          )

          if (result === 'cancel') {
            // Cancel All - 不做任何操作
            return
          } else if (result === 'middle') {
            // Pick Available Only - 只执行库存充足的订单
            // 需要过滤掉包含库存不足产品的订单
            const insufficientProductIds = new Set(insufficientList.map(i => i.productId))
            const availableOrders = rowData.filter((order: any) => {
              if (order.orderProduct && Array.isArray(order.orderProduct)) {
                return !order.orderProduct.some((p: any) => insufficientProductIds.has(p.productId))
              }
              return true
            })

            if (availableOrders.length === 0) {
              await SweetAlertService.errorMessage('No orders available to pick.')
              return
            }

            const ordersId = availableOrders.map((e: any) => e.orderId)
            ApiRequest({
              url: 'SalesOrder/ChangeOrderPickup?pickupFlag=1',
              method: 'put',
              data: ordersId,
            }).then(_ => {
              setTriggerResetData(getRandomKey())
            })
            return
          }
          // Continue - 继续执行所有订单
        }

        // 没有库存不足的问题，或者用户选择继续，执行所有订单
        const ordersId: any[] = [];
        rowData.map((e:any)=>{
          ordersId.push(e.orderId);
        })
        let url = `SalesOrder/ChangeOrderPickup?pickupFlag=1`;
        ApiRequest({
          url: url,
          method: 'put',
          data: ordersId,
        }).then(_ => {
          setTriggerResetData(getRandomKey())
        })
      }
    },
    // 批量完成按钮 - 只在非 Picking 页面显示
    ...(props.pickupFlag !== 1 ? [{
      icon: 'ghost',
      tooltip: 'Batch Finish',
      isFreeAction: false,
      onClick: async (event: any, rowData: any) => {
        if (Array.isArray(rowData)) {
          // 检查是否所有订单产品都已全部送达
          const unfinishedOrderProducts: any[] = [];

          rowData.forEach((order: any) => {
            if (order.orderProduct && Array.isArray(order.orderProduct)) {
              order.orderProduct.forEach((product: any) => {
                // 计算该产品是否全部送达
                const requiredQuantity = product.quantity * product.product?.baseProduct?.packagingType?.quantity;
                const deliveredBoxes = product.deliveredBoxes || 0;
                const isFullyDelivered = product.deliveredQuantity >= requiredQuantity || deliveredBoxes >= product.quantity;

                if (!isFullyDelivered) {
                  unfinishedOrderProducts.push({
                    orderId: order.orderId,
                    orderNo: order.orderNo,
                    customerName: order.customer?.company,
                    productName: product.product?.productName,
                    productCode: product.product?.productCode
                  });
                }
              });
            }
          });

          if (unfinishedOrderProducts.length > 0) {
            // 有订单产品未送达完成，显示提示
            // 按订单分组，显示公司名和产品代码
            const unfinishedOrdersMap = new Map<number, any>();

            unfinishedOrderProducts.forEach((item: any) => {
              if (!unfinishedOrdersMap.has(item.orderId)) {
                unfinishedOrdersMap.set(item.orderId, {
                  orderNo: item.orderNo,
                  companyName: item.customerName,
                  products: []
                });
              }
              unfinishedOrdersMap.get(item.orderId).products.push({
                productCode: item.productCode
              });
            });

            const unfinishedList = Array.from(unfinishedOrdersMap.values())
              .map((order: any) => {
                const products = order.products
                  .map((p: any) => `  • ${p.productCode}`)
                  .join('<br/>');
                return `<strong>${order.orderNo}</strong> - ${order.companyName}<br/>${products}`;
              })
              .join('<br/><br/>');

            const productCount = unfinishedOrderProducts.length;
            const orderCount = unfinishedOrdersMap.size;

            SweetAlertService.errorMessage(
              `<div style="text-align: left; line-height: 1.6;">
                <strong>${orderCount} Order(s) with ${productCount} product(s) not fully delivered:</strong><br/><br/>
                ${unfinishedList}
                <br/><br/>
                <strong>Please finish delivery before completing the orders.</strong>
              </div>`
            );
            return;
          }

          // 所有订单产品都已全部送达，继续执行批量 finish
          const result = await SweetAlertService.confirmMessage()
          if (result) {
            const finishOrders = async (orders: any[]) => {
              for (const order of orders) {
                await ApiRequest({
                  url: 'SalesOrder/FinishDispatch?id=' + order.orderId,
                  method: 'put'
                })
              }
              setTriggerResetData(getRandomKey())
            }
            await finishOrders(rowData)
          }
        }
      }
    }] : []),
  ], [props.pickupFlag])
  const isMultipleOfPackagingQty = (order:any) => {
      for (let orderProduct of order.orderProduct) {
          const baseProduct = orderProduct.product.baseProduct;
          const packagingQuantity = baseProduct.packagingType.quantity;
          const productQuantity = orderProduct.productQuantity;

          if (productQuantity % packagingQuantity !== 0) {
              return false; // 如果有一个不是整数倍，返回 false
          }
      }
    return true; // 如果所有都是整数倍，返回 true
}
  const getRenderData = (data: any) => {
    let renderData: any = []
    data.map((row: any) => {
	    renderData.push({
		    ...row,
		    cityId: row.deliveryCityId,
		    city: row.deliveryCity,
        requiredDate1: row.requiredDate,
        orderDate1: row.orderDate,
		    deliveryDate: row.deliveryDate && (new Date(row.deliveryDate + '.000Z')).toDateString(),
		    orderDate: row.orderDate && (new Date(row.orderDate + '.000Z')).toDateString(),
		    requiredDate: row.requiredDate && (new Date(row.requiredDate + '.000Z')).toDateString(),
        operEmployeeName:row.operEmployee?.firstName?row.operEmployee?.firstName.charAt(0).toUpperCase() + row.operEmployee?.firstName.slice(1):'Online',
        CreatedEmployeeName:row.createdEmployee?.firstName?row.createdEmployee?.firstName.charAt(0).toUpperCase() + row.createdEmployee?.firstName.slice(1):'',
	    })
      
    })
    if (props.userId) {
      renderData = renderData.filter((row:any) => {
        return row.customer.employeeId === props.userId ||row.employeeId === props.userId ;
      });
    }
    console.log(renderData)
    return renderData
  }

  // 使用 useCallback 稳定 onSelectionChange 引用
  const onSelectionChange = useCallback((newRows: any) => {
    console.log(newRows)
    // setCurrentOrderProductWithDetails(newRows)
  }, [])
  const styles = {
    actionButtonWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center', // Optional: Align child elements horizontally
    },
    actionButton: {
      margin: '10px 0', // Optional: Add margin between child elements
    },
  };
  const getTitle =()=> {
    if (props.userId) {
      return 'Outstanding Orders';
    } else if (props.pickupFlag === 1) {
      return 'Picking';
    } else {
      return 'Awaiting Dispatch';
    }
  }

  // 使用 useMemo 稳定 column 引用，避免定时刷新时 filter 被重置
  const columnModel = useMemo(() => setColumnModel(), [deliveryMethodLookup]);

  // 使用 useMemo 稳定 commonTablePageProps 引用
  const commonTablePageProps: CommonTablePagePropsInterface = useMemo(() => ({
    urlInfoKey: props.pickupFlag==1?urlKey.Pickupable:urlKey.AwaitingDispatch,
    title: getTitle(),
    column: columnModel,
    mappingRenderData: (data: any) => getRenderData(data),
    mappingUpdateData: async (dataDetail: any) => {
      if (dataDetail.orderStatusId >= 10) {
        const result = await SweetAlertService.confirmMessage('This order is in production or dispatch. Sure to delete?')
        if (!result) {
          return null
        }
      }
      dataDetail.paid = parseInt(dataDetail.paid, 10)
      dataDetail.deliveryCityId = dataDetail.cityId
      return dataDetail
    },
    triggerResetData: triggerResetData,
    actionButtons: props.userId?null:actionButtons,
    isNotEditable: true,
    isNotAddable: true,
    isNotDeletable: true,
    isEnableSelect:true,
    isShowSpinnerOnInit: isShowSpinner,
    onSelectionChange :  onSelectionChange,
    rowStyle: (rowData: any) => {
      const hasHold = rowData.orderProduct?.some((p: any) => p.thisQuantity !== 0);
      return hasHold ? {
        backgroundColor: '#8e8e8e18',
        borderTop: '1px solid #d0d0d0',
        borderBottom: '2px solid #ccc'
      } : {};
    },
    selectionProps: (rowData: any) => {
      // debugger
      // 在这里添加逻辑来决定是否禁用选择
      const disabled = !isMultipleOfPackagingQty(rowData)
      return {
        disabled: disabled,
        color: 'primary'
      };
    }
  }), [columnModel, triggerResetData, actionButtons, props.userId, props.pickupFlag, isShowSpinner, onSelectionChange])

  return (
    <div>
      {/* Color Legend */}
      {!props.userId && <div style={{
        backgroundColor: '#f0f9ff',
        padding: '12px 20px',
        marginBottom: '16px',
        borderRadius: '8px',
        border: '1px solid #bae6fd',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        alignItems: 'center'
      }}>
        <div style={{ fontWeight: 600, marginRight: '10px' }}>Color Legend:</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ color: 'black', fontWeight: 600 }}>■</span>
          <span style={{ fontSize: '13px' }}>Out of Stock (Stock = 0)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ color: '#FFA500', fontWeight: 600 }}>■</span>
          <span style={{ fontSize: '13px' }}>Partly Delivered/Dispatched</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ color: 'green', fontWeight: 600 }}>■</span>
          <span style={{ fontSize: '13px' }}>Sufficient Stock (Stock ≥ Order Quantity)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ color: '#0099ff', fontWeight: 600 }}>■</span>
          <span style={{ fontSize: '13px' }}>Partial Stock Available (0 &lt; Stock &lt; Order Quantity)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ color: 'red', fontWeight: 600 }}>■</span>
          <span style={{ fontSize: '13px' }}>Fully Delivered / Dispatched</span>
        </div>
      </div>}
      
      <CountdownDisplay refreshMinutes={refreshTime} isDialogOpen={open} />
      {!isLoading && <CommonTablePage {...commonTablePageProps} />}
      <CommonDialog title={dialogTitle} open={open} onDialogClose={onDialogClose} dialogContent={dialogContent} />
    </div>
  )
}

export default AwaitingDispatchList
